import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/** A turn's place in the thread: the customer on the left, anything the agent side said on the right. */
export const TurnRow = ({
  side,
  className,
  children,
}: {
  side: 'left' | 'right';
  className?: string;
  children: ReactNode;
}) => (
  <div className={cn('flex w-full', side === 'left' ? 'justify-start' : 'justify-end', className)}>
    <div className={cn('flex max-w-[80%] flex-col gap-1', side === 'left' ? 'items-start' : 'items-end')}>{children}</div>
  </div>
);

/** The 11px icon + text meta line above a turn bubble. */
export const TurnLabel = ({
  icon: Icon,
  className,
  iconClassName,
  children,
}: {
  icon: LucideIcon;
  className?: string;
  iconClassName?: string;
  children: ReactNode;
}) => (
  <div className={cn('flex items-center gap-1.5 px-1 text-[11px] text-muted-foreground', className)}>
    <Icon className={cn('size-3', iconClassName)} />
    {children}
  </div>
);

const BUBBLE_TONES = {
  customer: 'bg-gray-50 border-gray-100',
  agent: 'bg-indigo-50 border-indigo-100',
  /** The run's own reply, lifted off the thread. */
  ai: 'bg-indigo-50 border-indigo-100 ring-1 ring-indigo-200 shadow-[0_2px_10px_rgba(99,102,241,0.12)]',
  error: 'bg-red-50 border-red-200 text-red-700',
  warning: 'bg-amber-50/60 border-amber-200 text-amber-900',
} as const;

export const TurnBubble = ({
  tone,
  className,
  children,
}: {
  tone: keyof typeof BUBBLE_TONES;
  className?: string;
  children: ReactNode;
}) => (
  <div className={cn('rounded-2xl border px-3.5 py-2.5 text-sm', BUBBLE_TONES[tone], className)}>{children}</div>
);
